import { computed, shallowRef } from 'vue';

export interface DateRange {
  start: Date;
  end: Date;
  allowedDays: number[];
}

export function normalizeDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export const dateConfig = shallowRef({
  daysInFuture: 21,
});

// Helper function to generate season ranges dynamically based on current year
function generateSeasonRanges(): DateRange[] {
  return [
    {
      start: new Date(2025, 11, 1), // December 1 (current year)
      end: new Date(2026, 2, 7), // March 7 (next year)
      allowedDays: [1, 2, 4, 6, 0], // Monday, Tuesday, Thursday, Saturday, Sunday
    },
    {
      start: new Date(2026, 2, 8), // March 8 (current year)
      end: new Date(2026, 2, 26), // March 26 (current year)
      allowedDays: [1, 2, 4, 0], // Monday, Tuesday, Thursday, Sunday
    },
    {
      start: new Date(2026, 2, 27), // March 27 (current year)
      end: new Date(2026, 3, 21), // April 21 (current year)
      allowedDays: [1, 2, 4, 6, 0], // Monday, Tuesday, Thursday, Saturday, Sunday
    },
  ];
}

export const overriddedExcludedDates = computed(() => [
  normalizeDate(new Date(2026, 0, 18)), // January 18 (current year)
  normalizeDate(new Date(2026, 0, 25)), // January 25 (current year)
  normalizeDate(new Date(2026, 1, 1)), // February 1 (current year)
  normalizeDate(new Date(2026, 1, 8)), // February 8 (current year)
  normalizeDate(new Date(2026, 1, 15)), // February 15 (current year)
  normalizeDate(new Date(2026, 2, 29)), // March 29 (current year)
]);

export const overriddedIncludedDates = computed(() => [
  normalizeDate(new Date(2026, 2, 14)), // March 14 (current year)
  normalizeDate(new Date(2026, 2, 21)), // March 21 (current year)
]);

export const allowedRanges = computed(() => generateSeasonRanges());

export const seasonStart = computed(() => {
  const ranges = allowedRanges.value;
  return ranges.length > 0 ? ranges[0].start : new Date();
});

export const seasonEnd = computed(() => {
  const ranges = allowedRanges.value;
  return ranges.length > 0 ? ranges[ranges.length - 1].end : new Date();
});

// Utility functions
function getFirstAvailableDate(ranges: DateRange[]): Date {
  if (!ranges || ranges.length === 0) {
    return new Date();
  }

  const today = normalizeDate(new Date());
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + dateConfig.value.daysInFuture);

  const sortedRanges = [...ranges].sort((a, b) => a.start.getTime() - b.start.getTime());

  for (const range of sortedRanges) {
    const currentDate = normalizeDate(range.start > futureDate ? new Date(range.start) : new Date(futureDate));

    while (currentDate <= normalizeDate(range.end)) {
      const dayOfWeek = currentDate.getDay();

      if (range.allowedDays.includes(dayOfWeek)) {
        return new Date(currentDate);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return new Date();
}

function getLastAvailableDate(ranges: DateRange[]): Date {
  if (!ranges || ranges.length === 0) {
    return new Date();
  }

  for (let i = ranges.length - 1; i >= 0; i--) {
    const range = ranges[i];
    const currentDate = normalizeDate(new Date(range.end));

    while (currentDate >= normalizeDate(range.start)) {
      const dayOfWeek = currentDate.getDay();

      if (range.allowedDays.includes(dayOfWeek)) {
        return new Date(currentDate);
      }

      currentDate.setDate(currentDate.getDate() - 1);
    }
  }

  return new Date();
}

export const firstAllowedDate = computed(() => getFirstAvailableDate(allowedRanges.value));
export const lastAllowedDate = computed(() => getLastAvailableDate(allowedRanges.value));

export function isDateAllowed(date: Date, ranges: DateRange[] = allowedRanges.value): boolean {
  if (!date || !ranges || ranges.length === 0) return false;

  return ranges.some((range) => date >= range.start && date <= range.end && range.allowedDays.includes(date.getDay()));
}
